/* MaxBotix Refresh predictive search 1.9. Replaces assets/predictive-search.js.
   Uses assets/maxbotix-search-normalizer.js for aliases and accessory rules. */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.activeQuery = '';
    this.requestSequence = 0;
    this.lastRenderedQuery = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() { return this.input.value.trim(); }
  normalizedQuery(input) {
    return window.MaxBotixSearch?.normalize(input) || input;
  }
  recommendations(input) {
    return window.MaxBotixSearch?.recommendationIds(input) || [];
  }

  onChange() {
    super.onChange();
    const typed = this.getQuery();
    const nextQuery = this.normalizedQuery(typed);
    const baseChanged = nextQuery !== this.activeQuery;
    this.searchTerm = typed;
    if (!typed) {
      this.cancelPending();
      this.activeQuery = '';
      this.lastRenderedQuery = '';
      this.close(true);
      return;
    }
    if (baseChanged) {
      this.cancelPending();
      this.activeQuery = nextQuery;
      this.lastRenderedQuery = '';
      this.removeAttribute('results');
      this.predictiveSearchResults.replaceChildren();
      this.close();
    }
    // Even for the same base, reflect the newest original typed value.
    if (baseChanged || !this.hasAttribute('results')) this.getSearchResults(nextQuery);
    else {
      this.updateSearchForTerm();
      this.decorateCurrentResults();
    }
  }

  cancelPending() {
    this.requestSequence += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.cancelPending();
      this.searchTerm = '';
      this.activeQuery = '';
      this.lastRenderedQuery = '';
      this.closeResults(true);
      this.predictiveSearchResults.replaceChildren();
    }
  }

  onFocus() {
    const typed = this.getQuery();
    if (!typed) return;
    if (this.searchTerm !== typed || this.normalizedQuery(typed) !== this.activeQuery) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(this.activeQuery || this.normalizedQuery(typed));
  }

  onFocusOut() {
    setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm() {
    // Don't parse/replace an old query using RegExp. The Liquid section marks
    // the exact normalized term, so replace just that text safely.
    const label = this.querySelector('[data-predictive-search-search-for-text]');
    if (!label) return;
    const source = label.dataset.mbSearchTemplate || label.textContent;
    if (!label.dataset.mbSearchTemplate) label.dataset.mbSearchTemplate = source;
    const current = label.dataset.mbOriginalTerm || this.lastRenderedQuery;
    const displayed = this.getQuery();
    if (!current || !displayed) return;
    const pos = source.lastIndexOf(current);
    label.textContent = pos < 0 ? source : source.slice(0, pos) + displayed + source.slice(pos + current.length);
    // Always rebuild from saved server text, never transform previously modified text.
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const visible = Array.from(this.querySelectorAll('li[role="option"], button.predictive-search__item'))
      .filter(el => el.offsetParent !== null);
    if (!visible.length) return;
    const selected = this.querySelector('[aria-selected="true"]');
    const index = visible.indexOf(selected);
    if (direction === 'up' && index === -1) return;
    const next = direction === 'up' ? (index <= 0 ? visible.length - 1 : index - 1) : (index + 1) % visible.length;
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';
    if (selected) selected.setAttribute('aria-selected', 'false');
    visible[next].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', visible[next].id || '');
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selected) selected.click();
  }

  getSearchResults(query) {
    const term = this.normalizedQuery(query || this.getQuery());
    if (!this.getQuery()) return;
    this.activeQuery = term;
    const key = term.toLocaleLowerCase();
    this.setLiveRegionLoadingState();
    const sequence = ++this.requestSequence;
    if (Object.prototype.hasOwnProperty.call(this.cachedResults, key)) {
      this.renderSearchResults(this.cachedResults[key], term, sequence);
      return;
    }
    // The only fetch here is the Shopify native search-section endpoint.
    const controller = this.abortController;
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`, { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error(`Predictive search HTTP ${response.status}`); return response.text(); })
      .then(text => {
        if (sequence !== this.requestSequence || this.normalizedQuery(this.getQuery()) !== term) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Shopify predictive-search section missing');
        const html = section.innerHTML;
        this.allPredictiveSearchInstances.forEach(instance => { instance.cachedResults[key] = html; });
        this.renderSearchResults(html, term, sequence);
      })
      .catch(error => {
        if (error?.name === 'AbortError' || error?.code === 20 || sequence !== this.requestSequence) return;
        console.warn('[MaxBotix search] Predictive suggestions unavailable:', error);
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text || '';
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup, query, sequence) {
    if (sequence !== this.requestSequence || this.normalizedQuery(this.getQuery()) !== query) return;
    this.predictiveSearchResults.innerHTML = markup;
    this.lastRenderedQuery = query;
    this.setAttribute('results', true);
    this.updateSearchForTerm();
    this.decorateCurrentResults();
    this.setLiveRegionResults();
    this.open();
  }

  decorateCurrentResults() {
    const query = this.normalizedQuery(this.getQuery());
    if (!query || query !== this.activeQuery || !this.hasAttribute('results')) return;
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;
    const previous = list.querySelector('[data-mb-highlighted="primary"]');
    if (previous) {
      const current = previous.querySelector('.mb-predictive-match-original');
      if (current) current.textContent = 'Matched from “' + this.getQuery() + '” · ' + this.recommendations(this.getQuery())[0];
      return;
    }
    const targets = this.recommendations(this.getQuery());
    if (!targets.length) return;
    const existing = Array.from(list.querySelectorAll('li[role="option"]'));
    const findProduct = part => existing.find(li => {
      const heading = li.querySelector('.predictive-search__item-heading');
      return heading && (heading.textContent.toUpperCase().match(/MBP?\d{4,5}(?!\d)/g) || []).includes(part.toUpperCase());
    });
    const first = findProduct(targets[0]);
    if (!first) return;
    // Reuse the exact Shopify-rendered product image, link, and price markup.
    const card = first.cloneNode(true);
    card.id = 'mb-predictive-primary';
    card.dataset.mbHighlighted = 'primary';
    card.setAttribute('aria-selected', 'false');
    const anchor = card.querySelector('a[href]');
    if (!anchor) return;
    anchor.setAttribute('tabindex', '-1');
    const banner = document.createElement('strong');
    banner.className = 'mb-predictive-match-label';
    banner.textContent = (window.MaxBotixSearch?.isAccessory(targets[0]) ? 'Product match' : 'Sensor family match') + ' — ' + targets[0];
    const original = document.createElement('small');
    original.className = 'mb-predictive-match-original';
    original.textContent = 'Matching your search: ' + this.getQuery();
    // Keep the card as a two-column image + content row. Labels belong in
    // the content column; placing them as anchor children broke flex layout.
    const content = anchor.querySelector('.predictive-search__item-content');
    if (!content) return;
    banner.textContent = window.MaxBotixSearch?.isAccessory(targets[0]) ? 'Product match' : 'Sensor family match';
    original.textContent = 'Matched from “' + this.getQuery() + '” · ' + targets[0];
    content.prepend(banner, original);
    const action = document.createElement('span');
    action.className = 'mb-predictive-match-action';
    action.textContent = window.MaxBotixSearch?.isAccessory(targets[0])
      ? 'View product details →' : 'View product and configuration options →';
    content.append(action);
    list.prepend(card);
    // Remove the duplicate product row below the highlight.
    first.remove();
    // Related recommendations must come from real Shopify product HTML, never guessed handles.
    const secondary = targets[1] && findProduct(targets[1]);
    if (secondary) {
      const alt = secondary.cloneNode(true);
      alt.dataset.mbHighlighted = 'related';
      alt.id = 'mb-predictive-related';
      alt.setAttribute('aria-selected', 'false');
      const label = document.createElement('strong');
      label.className = 'mb-predictive-related-label';
      label.textContent = 'Also recommended — ' + targets[1];
      alt.querySelector('a')?.prepend(label);
      card.after(alt);
      secondary.remove();
    } else if (targets[1]) {
      this.fetchRelatedProduct(targets[1], this.activeQuery, this.requestSequence);
    }
  }

  fetchRelatedProduct(part, query, sequence) {
    // Only supplement the dropdown with a real, verified Shopify product.
    const prefix = window.Shopify?.routes?.root || '/';
    const url = `${prefix}search/suggest.json?q=${encodeURIComponent(part)}&resources[type]=product&resources[limit]=10`;
    fetch(url, {signal:this.abortController.signal}).then(response => {
      if (!response.ok) return null;
      return response.json();
    }).then(data => {
      if (!data || sequence !== this.requestSequence || this.normalizedQuery(this.getQuery()) !== query) return;
      const list = this.querySelector('#predictive-search-results-products-list');
      const primary = list?.querySelector('[data-mb-highlighted="primary"]');
      if (!primary || list.querySelector('[data-mb-highlighted="related"]')) return;
      const products = data?.resources?.results?.products || [];
      const product = products.find(item =>
        (String(item.title || '').toUpperCase().match(/MBP?\d{4,5}(?!\d)/g) || []).includes(part)
      );
      if (!product) return;
      let target;
      try { target = new URL(product.url, window.location.href); } catch (_) { return; }
      if (target.origin !== window.location.origin || !/\/products\//.test(target.pathname)) return;
      const li = document.createElement('li');
      li.id = 'mb-predictive-related';
      li.className = 'predictive-search__list-item';
      li.dataset.mbHighlighted = 'related';
      li.setAttribute('role','option');
      li.setAttribute('aria-selected','false');
      const link = document.createElement('a');
      link.href = target.pathname + target.search;
      link.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
      link.tabIndex = -1;
      const imgSrc = typeof product.featured_image === 'object' ? product.featured_image?.url : product.featured_image || product.image;
      if (imgSrc) {
        try {
          const imageURL = new URL(imgSrc, window.location.href);
          if (['https:', 'http:'].includes(imageURL.protocol)) {
            const img = document.createElement('img');
            img.className = 'predictive-search__image';
            img.src = imageURL.href; img.alt = ''; img.width = 50; img.height = 50; img.loading = 'lazy';
            link.appendChild(img);
          }
        } catch (_) { /* Product title/link are sufficient. */ }
      }
      const content = document.createElement('div');
      content.className = 'predictive-search__item-content';
      const heading = document.createElement('strong');
      heading.className = 'mb-predictive-related-label';
      heading.textContent = 'Also recommended — ' + part;
      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = product.title;
      content.append(heading,title);link.appendChild(content);li.appendChild(link);
      primary.after(li);
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[MaxBotix search] Related suggestion unavailable',error);
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const value = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(value?.textContent || 'Search suggestions loaded');
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    this.resultsMaxHeight = `${Math.max(240, window.innerHeight - (header?.getBoundingClientRect().bottom || 0))}px`;
    return this.resultsMaxHeight;
  }
  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }
  close(clear = false) { this.closeResults(clear); this.isOpen = false; }
  closeResults(clear = false) {
    if (clear) { this.input.value = ''; this.removeAttribute('results'); }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
customElements.define('predictive-search', PredictiveSearch);
